import type { Moment } from 'moment';
import type { LabelValue, Uuid } from 'store/reducers/types';
import type {
  PassingStepRawItem,
  RejectionNumberRawItem,
  RejectionReasonRawItem,
  TransitionStepRawResponse,
} from '_templates/AnalyticsDashboard/types';
import { fetchWrapper } from '../helpers/helpers';
import {
  AnalyticsFilterHelper,
  passingStepHelper,
  rejectionNumberHelper,
  rejectionReasonHelper,
  transitionStepHelper,
} from '../_templates/AnalyticsDashboard/helpers';

interface SelectOption {
  value: string;
  label?: string;
}

export interface AnalyticsFilterItem {
  uuid: Uuid;
  name: string;
  company_name?: string;
}

export interface AnalyticsTooltipEntry {
  name: string;
  data: number | string;
}

export interface RejectionNumberWidget {
  name: string;
  value: number;
  data: AnalyticsTooltipEntry[];
}

export interface RejectionReasonWidget {
  type: string;
  reasons: Array<{ name: string; value: number; data: AnalyticsTooltipEntry[] }>;
}

export interface TransitionStepWidget {
  options: Array<{
    type: string;
    candidates_count: number;
    relative_conversion?: string;
    absolute_conversion?: string;
  }>;
  empty: boolean;
}

export interface PassingStepWidget {
  label: string;
  value: string;
  data: number;
  index: number;
}

export interface AnalyticsFilterBody {
  candidates: SelectOption[];
  jobs: SelectOption[];
  offices: SelectOption[];
  openings_statuses: SelectOption[];
  recruiters: SelectOption[];
  recruiter_leads: SelectOption[];
  team_leads: SelectOption[];
  companies: SelectOption[];
  services: SelectOption[];
  period: [Moment, Moment];
}

export interface AnalyticsFilterPayload {
  candidates: string[];
  jobs: string[];
  offices: string[];
  openings_statuses: string[];
  recruiters: string[];
  recruiter_leads: string[];
  team_leads: string[];
  companies: string[];
  services: string[];
  from_date: string;
  to_date: string;
  search: string;
}

type AnalyticsFilterMapperInput = AnalyticsFilterBody & { search?: string };

export function analyticsFiltersMapper({ search, ...body }: AnalyticsFilterMapperInput): AnalyticsFilterPayload {
  return {
    candidates: body.candidates.map(s => s.value),
    jobs: body.jobs.map(s => s.value),
    offices: body.offices.map(s => s.value),
    openings_statuses: body.openings_statuses.map(s => s.value),
    recruiters: body.recruiters.map(s => s.value),
    recruiter_leads: body.recruiter_leads.map(s => s.value),
    team_leads: body.team_leads.map(s => s.value),
    companies: body.companies.map(s => s.value),
    services: body.services.map(s => s.value),
    from_date: body.period[0].format('YYYY-MM-DD'),
    to_date: body.period[1].format('YYYY-MM-DD'),
    search: search ?? '',
  };
}

export function analyticsFilter(
  url: string,
  body: AnalyticsFilterBody,
  search?: string,
  withoutModifications?: false
): Promise<LabelValue[]>;
export function analyticsFilter(
  url: string,
  body: AnalyticsFilterBody,
  search: string | undefined,
  withoutModifications: true
): Promise<AnalyticsFilterItem[]>;
export function analyticsFilter(
  url: string,
  body: AnalyticsFilterBody,
  search = '',
  withoutModifications?: boolean
): Promise<LabelValue[] | AnalyticsFilterItem[]> {
  return fetchWrapper<AnalyticsFilterItem[]>({
    url: `analytics/filters/${url}/`,
    method: 'POST',
    body: analyticsFiltersMapper({ search, ...body }),
  }).then(resp => (withoutModifications ? resp ?? [] : AnalyticsFilterHelper(resp ?? [])));
}

export type AnalyticsWidget = 'number' | 'reason' | 'transition' | 'passing';

export type AnalyticWidgetResult<W extends AnalyticsWidget> = W extends 'number'
  ? RejectionNumberWidget[]
  : W extends 'reason'
  ? RejectionReasonWidget[]
  : W extends 'transition'
  ? TransitionStepWidget
  : W extends 'passing'
  ? PassingStepWidget[]
  : unknown;

export function analyticWidgets<W extends AnalyticsWidget>(
  url: string,
  body: AnalyticsFilterBody,
  widget: W,
  query?: string
): Promise<AnalyticWidgetResult<W>>;
export function analyticWidgets(
  url: string,
  body: AnalyticsFilterBody,
  widget: string,
  query?: string
): Promise<unknown>;
export function analyticWidgets(
  url: string,
  { period, ...body }: AnalyticsFilterBody,
  widget: string,
  query = ''
): Promise<unknown> {
  return fetchWrapper({
    url: `analytics/widgets/${url}/${query}`,
    method: 'POST',
    body: {
      ...body,
      candidates: body.candidates.map(s => s.value),
      jobs: body.jobs.map(s => s.value),
      offices: body.offices.map(s => s.value),
      openings_statuses: body.openings_statuses.map(s => s.value),
      recruiters: body.recruiters.map(s => s.value),
      recruiter_leads: body.recruiter_leads.map(s => s.value),
      team_leads: body.team_leads.map(s => s.value),
      companies: body.companies.map(s => s.value),
      services: body.services.map(s => s.value),
      from_date: period[0].format('YYYY-MM-DD'),
      to_date: period[1].format('YYYY-MM-DD'),
    },
  }).then(resp => {
    if (widget === 'number') return rejectionNumberHelper(resp as RejectionNumberRawItem[]);
    if (widget === 'reason') return rejectionReasonHelper(resp as RejectionReasonRawItem[]);
    if (widget === 'transition') return transitionStepHelper(resp as TransitionStepRawResponse);
    if (widget === 'passing') return passingStepHelper(resp as PassingStepRawItem[]);
    return resp;
  });
}
